import isEmpty from 'lodash/isEmpty';
import uniqBy from 'lodash/uniqBy';
import { useQuery } from '@apollo/client';

import { getPosSyncedOffers } from 'shared/helpers/specials';
import { FilteredSpecialsDocument } from 'src/gql/graphql';
import type { Specials, Products } from 'src/gql/graphql';

export type ProductOffersInput = Pick<
  Products,
  'DispensaryID' | 'posDiscountNames' | 'POSPricingData' | 'posSyncedPricingApplied' | 'specialData'
>;

export default function useProductOffers(
  product: ProductOffersInput,
  menuType?: string | null
): { specials: Specials[]; loading: boolean; hasPosSyncedPricing: boolean } {
  // Mirrors the product card (use-card.ts). posSyncedPricingApplied is stamped server-side by applyPOSPrices
  // and is the source of truth for which source owns this product's offers on the active menu: when true,
  // POS-synced discounts own them and legacy bogoSpecials are ignored entirely -- even if that leaves no
  // offers to show. Without a menuType, either menu being applied counts.
  const { medical: medicalApplied, recreational: recreationalApplied } = product.posSyncedPricingApplied ?? {};
  let hasPosSyncedPricing = Boolean(medicalApplied || recreationalApplied);
  if (menuType === 'med') {
    hasPosSyncedPricing = Boolean(medicalApplied);
  } else if (menuType === 'rec') {
    hasPosSyncedPricing = Boolean(recreationalApplied);
  }
  // posDiscountNames is already scoped to the menus whose POS prices were applied.
  const posDiscountNames = (product.posDiscountNames ?? []).filter((name): name is string => Boolean(name));

  // Only legacy offers the product actually QUALIFIES for are fetched and rendered, so a special attached
  // with empty qualifyingOptions (e.g. a 7g product against an eighths-only BOGO) is neither.
  const qualifyingLegacyOffers = (product.specialData?.bogoSpecials ?? []).filter(
    (special) => special?.qualifyingOptions && special.qualifyingOptions.length > 0
  );

  const { data, loading } = useQuery(FilteredSpecialsDocument, {
    skip: hasPosSyncedPricing || isEmpty(qualifyingLegacyOffers),
    variables: {
      specialsFilter: {
        dispensaryId: product.DispensaryID,
        current: true,
        specialIds: qualifyingLegacyOffers.map((s) => s?.specialId).filter((id): id is string => id != null),
      },
    },
  });

  if (hasPosSyncedPricing) {
    // Build lightweight Specials-shaped stand-ins directly from POSPricingData rather than a second
    // round-trip. There's no Specials Mongo document backing these ids, but the offer card components only
    // ever read _id/menuDisplayName/name/specialType off a special, so this is sufficient to render and link
    // them. POSPricingData carries both menu buckets, so restrict to the names the card shows, and collapse
    // by name (posDiscountNames is deduped by name server-side) so the counts match.
    const posSyncedOffers = uniqBy(
      getPosSyncedOffers(product).filter(({ menuDisplayName }) => posDiscountNames.includes(menuDisplayName)),
      'menuDisplayName'
    ).map(
      ({ id, menuDisplayName }) =>
        (({
          _id: id,
          specialId: id,
          menuDisplayName,
          name: menuDisplayName,
          specialType: 'bogo',
        } as unknown) as Specials)
    );

    return { specials: posSyncedOffers, loading: false, hasPosSyncedPricing };
  }

  const specials = (data?.filteredSpecials?.specials ?? []).filter((s): s is Specials => s != null);

  return { specials, loading, hasPosSyncedPricing };
}
